/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { ChevronDown, ChevronRight, Trash } from '@saleshandy/icons';
import Input from '../../../../shared/design-system/components/input';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';

interface FieldErrors {
  heading?: string;
  description?: string;
}

export const validateFields = (
  heading: string,
  description: string,
): FieldErrors => {
  const errors: FieldErrors = {};

  const trimmedHeading = heading.trim();
  const trimmedDescription = description.trim();

  if (trimmedHeading.length < 1 || trimmedHeading.length > 200) {
    errors.heading = 'Name must be between 1 and 200 characters.';
  }

  if (trimmedDescription.length < 10 || trimmedDescription.length > 1000) {
    errors.description = 'Description must be between 10 and 1000 characters.';
  }

  return errors;
};

type SelectionType = 'radio' | 'checkbox';

interface SelectableCardProps {
  type: SelectionType;
  selected: boolean;
  onSelect: () => void;
  heading: string;
  description: string;
  isNew?: boolean;
  onUpdate: (newHeading: string, newDescription: string) => void;
  expanded: boolean;
  onExpand: () => void;
  onDelete?: () => void;
  onCollapse?: () => void;
  placeholderHeading?: string;
  placeholderDescription?: string;
  validateRef?: React.MutableRefObject<() => boolean>;
  headingLabel?: string;
  descriptionLabel?: string;
  dummyHeading?: string;
  dummyDescription?: string;
  onValidationResult?: (isError: boolean) => void;
}

const EditableField: React.FC<{
  label: string;
  type: 'input' | 'textarea';
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (value: string) => void;
  onFieldBlur: () => void;
  placeholder?: string;
}> = ({
  label,
  type,
  value,
  error,
  touched,
  onChange,
  onFieldBlur,
  placeholder,
}) => (
  <>
    <label
      className={`input-label ${
        type === 'input' ? '' : 'input-field-textarea'
      }`}
    >
      {label}
    </label>
    {type === 'input' ? (
      <Input
        className={`input-field ${error ? 'error' : ''}`}
        value={value}
        onChange={onChange}
        onBlur={onFieldBlur}
        placeholder={placeholder || ''}
      />
    ) : (
      <textarea
        className={`input-field ${error ? 'error' : ''}`}
        value={value}
        placeholder={placeholder || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onFieldBlur}
      />
    )}
    {touched && error && <p className="field-error">{error}</p>}
  </>
);

export const AccordionCard: React.FC<SelectableCardProps> = ({
  type,
  selected,
  onSelect,
  heading,
  description,
  isNew = false,
  onUpdate,
  expanded,
  onExpand,
  onDelete,
  onCollapse,
  placeholderDescription,
  placeholderHeading,
  validateRef,
  headingLabel,
  descriptionLabel,
  dummyHeading,
  dummyDescription,
  onValidationResult,
}) => {
  const [localHeading, setLocalHeading] = useState(heading);
  const [localDescription, setLocalDescription] = useState(description);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState({
    heading: false,
    description: false,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  const validateAndUpdate = useCallback(() => {
    const validationErrors = validateFields(localHeading, localDescription);
    setErrors(validationErrors);
    const hasErrors = Object.keys(validationErrors)?.length > 0;

    onValidationResult?.(hasErrors);
    if (!hasErrors) {
      onUpdate(localHeading, localDescription);
      return true;
    }
    return false;
  }, [localHeading, localDescription, onUpdate, onValidationResult]);

  const handleHeadingBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, heading: true }));
    if (!errors.heading && !errors.description) {
      onUpdate(localHeading, localDescription);
    }
  }, [localHeading, localDescription, errors, onUpdate]);

  const handleDescriptionBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, description: true }));
    if (!errors.heading && !errors.description) {
      onUpdate(localHeading, localDescription);
    }
  }, [localHeading, localDescription, errors, onUpdate]);

  const handleHeadingChange = useCallback(
    (value: string) => {
      setLocalHeading(value);
      const headingError = validateFields(value, localDescription).heading;
      setErrors((prev) => ({ ...prev, heading: headingError }));
      setTouched((prev) => ({ ...prev, heading: true }));
      onValidationResult?.(!!headingError || !!errors.description);
    },
    [localDescription, errors.description, onValidationResult],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setLocalDescription(value);
      const descriptionError = validateFields(localHeading, value).description;
      setErrors((prev) => ({ ...prev, description: descriptionError }));
      setTouched((prev) => ({ ...prev, description: true }));
      onValidationResult?.(!!descriptionError || !!errors.heading);
    },
    [localHeading, errors.heading, onValidationResult],
  );

  useEffect(() => {
    if (validateRef) validateRef.current = validateAndUpdate;
  }, [validateRef, validateAndUpdate]);

  useEffect(() => {
    setLocalHeading(heading);
    setLocalDescription(description);
  }, [heading, description]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        const isValid = validateAndUpdate();
        if (!isValid) {
          setTouched({ heading: true, description: true });
        }
      }
    };

    if (expanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    expanded,
    localHeading,
    localDescription,
    heading,
    description,
    onUpdate,
    onCollapse,
  ]);

  return (
    <div
      className={`card-container ${selected ? 'selected' : ''} ${
        expanded ? 'expanded' : ''
      } ${errors.heading || errors.description ? 'error' : ''}`}
      ref={wrapperRef}
      {...accessibleOnClick((e) => {
        const target = e.target as HTMLElement;
        if (!['INPUT', 'TEXTAREA', 'LABEL']?.includes(target?.tagName)) {
          onExpand();
        }
      })}
    >
      <div className="card-top-section">
        <div className="card-top-section-left">
          <div
            className="radio-wrapper"
            {...accessibleOnClick((e) => e.stopPropagation())}
          >
            {type === 'checkbox' ? (
              <Checkbox
                checked={selected}
                onChange={onSelect}
                className="cursor-pointer"
              />
            ) : (
              <Input
                type="radio"
                checked={selected}
                onChange={onSelect}
                className="cursor-pointer"
              />
            )}
          </div>

          <div className="content">
            <div className="title">{heading || dummyHeading}</div>
            <div className="description">{description || dummyDescription}</div>
          </div>
        </div>

        <div
          className="card-top-section-right"
          {...accessibleOnClick(onExpand)}
        >
          {isNew && onDelete && (
            <OverlayTooltip text="Remove" placement={Placement.Bottom}>
              <div className="delete-icon" {...accessibleOnClick(onDelete)}>
                <Trash />
              </div>
            </OverlayTooltip>
          )}
          <div className="chevron-icon" {...accessibleOnClick(onExpand)}>
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </div>
        </div>
      </div>

      <div className={`content form-section ${expanded ? 'show' : 'hide'}`}>
        <EditableField
          label={headingLabel}
          type="input"
          value={localHeading}
          error={errors.heading}
          touched={touched.heading}
          onChange={handleHeadingChange}
          onFieldBlur={handleHeadingBlur}
          placeholder={placeholderHeading}
        />

        <EditableField
          label={descriptionLabel}
          type="textarea"
          value={localDescription}
          error={errors.description}
          touched={touched.description}
          onChange={handleDescriptionChange}
          onFieldBlur={handleDescriptionBlur}
          placeholder={placeholderDescription}
        />
      </div>
    </div>
  );
};

export default memo(AccordionCard);
